import React, { useMemo, useRef } from "react";

import moment from "moment";
import Timeline from "okast-timeline";
import Timeslot from "okast-timeslot";
import { v4 as uuidv4 } from "uuid";

import { TIME_FORMAT } from "../../../constants/dateFormat";
import { MEDIA_LIBRARY_ITEM } from "../../../constants/fast";
import { AD_BREAK, TIMESLOT } from "../../../constants/media";
import useFAST from "../../../contexts/FASTContext";
import useKeyPress from "../../../hooks/useKeyPress";
import useMediaColorByFormat from "../../../hooks/useMediaColorByFormat";
import { getTemplateDropZoneItems } from "../../../utils/fast";
import ItemRenderer from "../timeline/ItemRenderer";
import {
  computeItemEndTimeLoopTimeline,
  MAX_ZOOM_OUT_DURATION,
  MIN_ZOOM_IN_DURATION,
  TimelineRenderTypes,
} from "../timeline/timeline";
import TimelineHeader from "../timeline/TimelineHeader";
import useTimeChange from "../timeline/useTimeChange";

import type {
  OnItemDragObjectMove,
  OnItemDragObjectResize,
  TimelineProps,
} from "okast-timeline";
import type { Slot } from "okast-timeslot";
import type { Component, JSX } from "react";

import type { MediaLibraryDragItem } from "../../../types";
import type { OnDropItemParams } from "../timeline/ItemRenderer";
import type {
  AllTemplateItems,
  TemplateDropzoneItem,
  TemplateTimelineItem,
  TimelineRef,
} from "../timeline/timeline.types";

export const GROUP_ID = "1";

export const DEFAULT_START_TIME = "00:00:00";

interface TemplateTimelineProps {
  items: AllTemplateItems[];
  updateItems: (items: AllTemplateItems[]) => void;
}

function TemplateTimeline({
  items,
  updateItems,
}: TemplateTimelineProps): JSX.Element {
  const ref = useRef<
    (Component<TimelineProps<AllTemplateItems>> & TimelineRef) | null
  >(null);
  const isShiftKeyPress = useKeyPress("Shift");
  const { state } = useFAST();

  const { channelData } = state;

  const dailyStartTime = channelData?.daily_start_time ?? DEFAULT_START_TIME;

  const MIN_TIME = moment(dailyStartTime, TIME_FORMAT)
    .add(1, "month")
    .valueOf();

  const MAX_TIME = moment(dailyStartTime, TIME_FORMAT)
    .add(1, "month")
    .add(24, "hours")
    .valueOf();

  const { handleTimeChange, visibleTimeStart, visibleTimeEnd } = useTimeChange({
    minTime: MIN_TIME,
    maxTime: MAX_TIME,
  });

  const getMediaColor = useMediaColorByFormat();

  const groups = [{ id: GROUP_ID, title: "" }];

  const usedSlots: Slot[] = useMemo(
    () =>
      items.map((item) => ({
        start: item.start_time.valueOf(),
        end: item.end_time.valueOf(),
        id: item.id,
      })),
    [items],
  );

  const timeslot = useMemo(
    () =>
      new Timeslot({
        maxRange: {
          start: MIN_TIME,
          end: MAX_TIME,
        },
        usedSlots,
        id: GROUP_ID,
        isLoop: true,
      }),
    [usedSlots, MIN_TIME, MAX_TIME],
  );

  const dropZoneItems = getTemplateDropZoneItems(timeslot);

  const dropzoneAcceptedFormats = [
    `${MEDIA_LIBRARY_ITEM}-unknown`,
    `${MEDIA_LIBRARY_ITEM}-${AD_BREAK}`,
  ];

  const getItemType = (
    itemType: MediaLibraryDragItem["type"],
  ): typeof TIMESLOT | typeof AD_BREAK | null => {
    if (itemType === TIMESLOT) {
      return TIMESLOT;
    }

    if (itemType === AD_BREAK) {
      return AD_BREAK;
    }

    return null;
  };

  const addItem = ({ item, startTime }: OnDropItemParams): void => {
    const itemType = getItemType(item.type);

    const color = itemType === AD_BREAK ? getMediaColor(AD_BREAK) : item.color;

    if (!itemType || !item.color) return;

    const newItem: TemplateTimelineItem = {
      id: uuidv4(),
      mediaId: item.mediaId,
      group: GROUP_ID,
      title: item.label,
      start_time: moment(startTime),
      end_time: moment(startTime.valueOf() + item.duration),
      canMove: true,
      duration: item.duration,
      type: itemType,
      color,
    };

    updateItems([...items, newItem]);
  };

  const handleItemMove = async (
    itemId: string,
    dragTime: number,
  ): Promise<void> => {
    const itemsUpdated = items.map((item) => {
      if (item.id === itemId) {
        return {
          ...item,
          start_time: moment(dragTime),
          end_time: moment(dragTime + item.duration),
        };
      }

      return item;
    });

    updateItems(itemsUpdated);
  };

  const handleItemDrag = (
    itemDragObject: OnItemDragObjectMove | OnItemDragObjectResize,
  ): void => {
    const { itemId, time } = itemDragObject;

    const itemsUpdated = items.map((item) => {
      if (item.id === itemId) {
        return {
          ...item,
          start_time: moment(time),
          end_time: moment(time + item.duration),
        };
      }

      return item;
    });

    updateItems(itemsUpdated);
  };

  const moveResizeValidator = (
    _: "move" | "resize",
    item: TemplateTimelineItem,
    time: number,
  ): number => {
    const itemEndTime = computeItemEndTimeLoopTimeline({
      startTime: time,
      duration: item.duration,
      minTimeStart: MIN_TIME,
      maxTimeEnd: MAX_TIME,
    });
    const availableStartDate = timeslot.getAvailableStartDate({
      start: time,
      end: itemEndTime,
      id: item.id,
    });

    return availableStartDate ?? item.start_time.valueOf();
  };

  const replaceItem = (
    itemToDelete: TemplateTimelineItem,
    itemToAdd: MediaLibraryDragItem,
  ): void => {
    if (itemToAdd?.mediaId) {
      const startTime = itemToDelete.start_time;

      const itemsUpdated: (TemplateTimelineItem | TemplateDropzoneItem)[] =
        items.map((item) => {
          if (item.id === itemToDelete.id) {
            const itemType = getItemType(itemToAdd.type);

            if (!itemType || !itemToAdd.color) return item;

            return {
              id: uuidv4(),
              mediaId: itemToAdd.mediaId,
              group: GROUP_ID,
              title: itemToAdd.label,
              start_time: moment(startTime),
              end_time: moment(startTime.valueOf() + itemToAdd.duration),
              canMove: true,
              duration: itemToAdd.duration,
              type: itemType,
              color: itemToAdd.color,
            };
          }
          return item;
        });

      updateItems(itemsUpdated);
    }
  };

  const deleteItem = (itemId: string): void => {
    const itemsUpdated = items.filter((item) => item.id !== itemId);

    updateItems(itemsUpdated);
  };

  return (
    <div className="relative size-full">
      <Timeline
        ref={ref}
        isLoop
        isVertical
        buffer={1}
        canChangeGroup={false}
        canResize={false}
        canWheelZoom={false}
        dragSnap={5 * 60 * 1000}
        groups={groups}
        items={[...items, ...dropZoneItems]}
        itemSizeRatio={0.9}
        maxTimeEnd={MAX_TIME}
        maxZoomOutDuration={MAX_ZOOM_OUT_DURATION}
        minTimeStart={MIN_TIME}
        minZoomInDuration={MIN_ZOOM_IN_DURATION}
        moveResizeValidator={moveResizeValidator}
        onItemDrag={handleItemDrag}
        onItemMove={handleItemMove}
        onTimeChange={handleTimeChange}
        sidebarSize={0}
        visibleTimeEnd={visibleTimeEnd}
        visibleTimeStart={visibleTimeStart}
        itemRenderer={({ itemContext, item, getItemProps }): JSX.Element => (
          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          // @ts-ignore
          <ItemRenderer
            dropzoneAcceptedFormats={dropzoneAcceptedFormats}
            getItemProps={getItemProps}
            isReplaceMode={isShiftKeyPress}
            item={item}
            itemContext={itemContext}
            onDeleteItem={deleteItem}
            onDropItem={addItem}
            onReplaceItem={replaceItem}
            renderType={TimelineRenderTypes.TEMPLATING}
            timelineRef={ref}
          />
        )}
      >
        <TimelineHeader headerSize={80} sidebarSize={0} timelineRef={ref} />
      </Timeline>
    </div>
  );
}

export default TemplateTimeline;
